import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import type { MemberRole } from '../api/backend'

/**
 * Role-based access guard hook.
 *
 * Waits for the full initialization chain to complete before evaluating access:
 *   1. cookie-session bootstrap            (AuthContext)
 *   2. GET /member                         (workspace + role resolution)
 *
 * Redirects to `/admin/unauthorized` if the resolved role is not in
 * `allowedRoles`. Must be called inside a component wrapped by
 * `AuthProvider` and `WorkspaceProvider`.
 *
 * @param allowedRoles - Roles permitted to access the current route
 * @returns `{ isAllowed, isLoading }` — use to conditionally render content
 */
export function useRoleGuard(allowedRoles: MemberRole[]): {
  isAllowed: boolean
  isLoading: boolean
} {
  const { isLoading: isAuthLoading } = useAuth()
  const { isResolved, memberRole } = useWorkspace()
  const navigate = useNavigate()

  // Still loading if auth session is pending OR workspace init (workspaces +
  // member) hasn't completed yet.
  const isLoading = isAuthLoading || !isResolved

  const isAllowed = isResolved && memberRole !== null && allowedRoles.includes(memberRole)

  useEffect(() => {
    if (isLoading) return
    if (!isAllowed) {
      navigate('/admin/unauthorized', { replace: true })
    }
  }, [isLoading, isAllowed, navigate])

  return { isAllowed, isLoading }
}
